// `apps/web` analytics adapter — Web Wave 9 (US-245 / US-246).
//
// Re-exports the typed event taxonomy from `@barel/telemetry/web` and adds a
// `bootAnalytics()` helper that:
//   1. Wires Vite-injected env vars into the shared `setAnalyticsEnv()` slot.
//   2. Eagerly initialises PostHog with autocapture ON, capture_pageview ON,
//      and session recording OFF (privacy posture from Web Wave 9).
//
// Hard rule from the build prompt: **no user toggle**. Mandatory telemetry
// per P12; the redaction allowlist + `disable_session_recording: true` are
// the privacy guarantee, not a Settings → Privacy switch.

import { analytics, identify, reset, setAnalyticsEnv, track } from '@barel/telemetry/web';

interface BootAnalyticsOptions {
  posthogKey?: string | undefined;
  posthogHost?: string | undefined;
}

/**
 * Initialise PostHog with the Wave 9 baseline. Silent no-op when
 * `posthogKey` is empty — keeps local-dev / E2E runs quiet.
 *
 * Returns `true` if a real init happened, `false` if we no-op'd.
 */
export function bootAnalytics(options: BootAnalyticsOptions = {}): boolean {
  setAnalyticsEnv({
    POSTHOG_PROJECT_API_KEY: options.posthogKey,
    POSTHOG_HOST: options.posthogHost,
  });
  if (!options.posthogKey) return false;
  // First track() call lazily initialises the underlying client. We fire a
  // synthetic `app.boot` event so the SDK loads + the first auto-capture
  // hooks attach before the user clicks anything.
  track('app.boot');
  return true;
}

/** Re-export the typed event taxonomy so call sites can `analytics.signup({...})`. */
export { analytics, identify, reset, track };
